// src/components/Departments/FormAddEditDepartments.jsx
import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme, Switch, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import { useCreateDepartment } from "../../hooks/Departments/useCreateDepartment";
import { useUpdateDepartment } from "../../hooks/Departments/useUpdateDepartment";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";

const FormAddEditDepartments = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [slug, setSlug] = useState("");
    // const [sortOrder, setSortOrder] = useState(0);
    const [isActive, setIsActive] = useState(true);

    const { createDepartment, isCreating } = useCreateDepartment();
    const { updateDepartment, isUpdating } = useUpdateDepartment();

    const loadingSubmit = isCreating || isUpdating;


    useEffect(() => {
        if (nameEn) {
            setSlug(slugify(nameEn));
        }
    }, [nameEn]);

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setSlug(currentData.slug || "");
            // setSortOrder(currentData.sort_order || 0);
            setIsActive(currentData.is_active ?? true);
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();
        const payload = {
            name: { ar: nameAr || nameEn, en: nameEn || nameAr },
            slug: slug,
            // sort_order: Number(sortOrder),
            sort_order: isEdit ? currentData.sort_order : null,
            is_active: isActive,
        };

        if (isEdit && currentData?.id) {
            updateDepartment(
                { id: currentData.id, data: payload },
                { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } }
            );
        } else {
            createDepartment(payload, {
                onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); }
            });
        }
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل قسم" : "Edit Department") : (isArabic ? "إضافة قسم جديد" : "Create New Department")}
                    </Typography>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="الاسم (عربي)"
                                placeholder="أدخل اسم القسم بالعربي"
                                type="text"
                                name="name_ar"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Name (English)"
                                placeholder="Enter department name in English"
                                type="text"
                                name="name_en"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug)" : "Slug"}
                            placeholder="electronics-gadgets"
                            type="text"
                            name="slug"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            disabled={true}
                            required={true}
                        />
                    </Grid>

                    {/* <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الترتيب" : "Sort Order"}
                            type="number"
                            name="sort_order"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                        />
                    </Grid> */}

                    <Grid item xs={12} md={6}>
                        <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={isArabic ? "حالة القسم (نشط)" : "Department Status (Active)"}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditDepartments;
